import { useMemo } from "react";

import { AllValueType, NullableRecord, SortDirection, filterNullableRecord, isAll } from "@bridgesplit/utils";
import { useMemoizedGroupedMap } from "@bridgesplit/ui";
import Decimal from "decimal.js";

import { useLendingStrategySummariesQuery, useLendingStrategyInfosQuery, useUserStrategiesQuery } from "../reducers";
import { calculateFixedStrategyApy, calculateWAvgApy, useAbfTypesToUiConverter } from "../utils";
import {
    LendingStrategyFilter,
    LendingStrategyPagination,
    StrategiesAggregate,
    StrategyExpanded,
    StrategyExpandedInfo,
    StrategyInfo,
    StrategyInfoResponse,
    StrategySortType,
    UserStrategiesFilter,
    UserStrategyGroup
} from "../types";
import { useMarketOracleInfos } from "./markets";
import { useGenerateUiExpanded } from "../utils/ui-expanded";
import { useLoopscaleWalletContext } from "../wallet";
import { MAXIMUM_PAGE_SIZE } from "../constants";

export type StrategiesResult = {
    data: StrategyExpandedInfo[] | undefined;
    totalStrategies: number | undefined;
    aggregate: StrategiesAggregate | undefined;
    snapshotTs: number | undefined;
    isLoading: boolean;
    isFetching: boolean;
};

function useNormalizedStrategyFilter(filter: Partial<LendingStrategyPagination>): LendingStrategyPagination {
    return useMemo(
        () => ({
            sortType: filter.sortType ?? StrategySortType.NetAssetValue,
            sortDirection: filter.sortDirection ?? SortDirection.Asc,
            page: filter.page ?? 0,
            pageSize: filter.pageSize ?? 25,
            ...filter
        }),
        [filter]
    );
}

const EMPTY_STRATEGIES_AGGREGATE: StrategiesAggregate = {
    count: 0,
    principalDeployedUsd: 0,
    strategyBalanceUsd: 0,
    totalSupplyUsd: 0,
    interestAccruedUsd: 0,
    interestEarnedAllTimeUsd: 0,
    wAvgApy: 0,
    expected24hInterestUsd: 0
};

/** Wrap pre-fetched strategy items (e.g. inlined on /lending_vaults/info) in the
 * StrategyInfoResponse shape so useExpandStrategyResponse can expand them with no
 * network call. pageInfo/aggregate are placeholders — list callers don't read them. */
export function buildStrategyInfoResponse(items: StrategyInfo[]): StrategyInfoResponse {
    return {
        items,
        pageInfo: { page: 0, pageSize: items.length, totalItems: items.length, totalPages: 1 },
        aggregate: EMPTY_STRATEGIES_AGGREGATE,
        snapshotTs: 0
    };
}

/** Shared post-processing for the standard and summary strategy hooks: token
 * metadata merge, market oracle lookup, computed APY fields, collateral
 * expansion. Both endpoints return the same `StrategyInfoResponse` shape. */
export function useExpandStrategyResponse(
    rawData: StrategyInfoResponse | undefined,
    queryLoading: boolean,
    isFetching: boolean,
    skip: boolean | undefined
): StrategiesResult {
    const items = useMemo(() => rawData?.items ?? [], [rawData]);

    const { tokensLoading, getMetadata, convertStrategyInfo } = useAbfTypesToUiConverter();
    const { createStrategyCollateralExpanded, isLoading: isUiLoading } = useGenerateUiExpanded();

    const { data: marketData, isLoading: marketLoading } = useMarketOracleInfos({
        filter: {
            principalMints: [],
            addresses: items.map((s) => s.strategy.marketInformation)
        }
    });

    const isLoading = queryLoading || tokensLoading || isUiLoading || marketLoading;

    const data = useMemo(() => {
        if (isLoading) return undefined;
        if (skip || !items) return [];
        return items
            .map((d): NullableRecord<StrategyExpandedInfo> => {
                const strategyInfo = convertStrategyInfo(d);

                const fixedApy = calculateFixedStrategyApy(strategyInfo);
                const wAvgApy = calculateWAvgApy(strategyInfo, fixedApy);

                const lendingStrategyExpanded: StrategyExpanded = {
                    ...strategyInfo.strategy,
                    interestPerSecond: new Decimal(strategyInfo.strategy.interestPerSecond),
                    fixedApy,
                    wAvgApy
                };

                const strategyMarketInfo = marketData?.find(
                    (info) => info.marketInformation.address === strategyInfo.strategy.marketInformation
                );

                const collateral = createStrategyCollateralExpanded(strategyInfo, strategyMarketInfo);

                const principalMetadata = getMetadata(d.strategy.principalMint);

                return {
                    ...strategyInfo,
                    totalBalance:
                        (strategyInfo.externalYieldInfo?.balance ?? 0) + (strategyInfo.strategy.tokenBalance ?? 0),
                    collateral,
                    principalMetadata,
                    strategy: lendingStrategyExpanded
                };
            })
            .filter(filterNullableRecord);
    }, [isLoading, skip, items, marketData, convertStrategyInfo, createStrategyCollateralExpanded, getMetadata]);

    return {
        data: isLoading ? undefined : data,
        totalStrategies: rawData?.pageInfo.totalItems,
        aggregate: rawData?.aggregate,
        snapshotTs: rawData?.snapshotTs,
        isLoading,
        isFetching
    };
}

export function useStrategies(filter: Partial<LendingStrategyPagination>, skip?: boolean): StrategiesResult {
    const rawFilter = useNormalizedStrategyFilter(filter);
    const { data: rawData, isLoading: queryLoading, isFetching } = useLendingStrategyInfosQuery(rawFilter, { skip });
    return useExpandStrategyResponse(rawData, queryLoading, isFetching, skip);
}

export function useLenderStrategies(params?: { skip?: boolean; filter?: LendingStrategyFilter }): StrategiesResult {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    return useStrategies({ ...params?.filter, userAddress: activeWallet }, !activeWallet || params?.skip);
}

/** Summary variant of `useStrategies`. `terms.assetTerms` is empty unless the
 * caller passes `collateralTermsAssetFilter` — see backend route docs. */
export function useStrategySummaries(filter: Partial<LendingStrategyPagination>, skip?: boolean): StrategiesResult {
    const rawFilter = useNormalizedStrategyFilter(filter);
    const {
        data: rawData,
        isLoading: queryLoading,
        isFetching
    } = useLendingStrategySummariesQuery(rawFilter, { skip });
    return useExpandStrategyResponse(rawData, queryLoading, isFetching, skip);
}

export function getFirstStrategy(strategies: StrategyExpandedInfo[] | undefined): StrategyExpandedInfo | undefined {
    return strategies?.[0];
}

/**
 * Fetch the active wallet's strategies across the given principal mints in one batched
 * query, returning the flat list plus a per-mint grouping. Pair with `useUserStrategies`
 * (mint-grouped summaries) to render a grouped table whose detail rows are available
 * immediately — no per-group lazy fetch on expand.
 *
 * Capped at `MAXIMUM_PAGE_SIZE`. If the active wallet has more strategies under the
 * given mints than the cap, the trailing rows are silently omitted, and any "select all"
 * built on this hook will only cover the returned subset.
 */
export function useStrategiesGroupedByMint(
    principalMints: string[],
    options?: { skip?: boolean }
): {
    allDetails: StrategyExpandedInfo[] | undefined;
    detailsByMint: Map<string, StrategyExpandedInfo[]> | undefined;
    isLoading: boolean;
} {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const filter = useMemo(
        (): Partial<LendingStrategyPagination> => ({
            userAddress: activeWallet,
            principalMints,
            addresses: [],
            showArchived: false,
            sortType: StrategySortType.NetAssetValue,
            sortDirection: SortDirection.Asc,
            page: 0,
            pageSize: MAXIMUM_PAGE_SIZE
        }),
        [activeWallet, principalMints]
    );

    const skip = options?.skip || !activeWallet || principalMints.length === 0;
    const { data: allDetails, isLoading } = useStrategies(filter, skip);

    const detailsByMint = useMemoizedGroupedMap<StrategyExpandedInfo, string>(
        allDetails,
        (d: StrategyExpandedInfo) => d.strategy.principalMint
    );

    return { allDetails, detailsByMint, isLoading };
}

export function useUserStrategiesPaginated({
    pagination,
    principalGroupKey,
    skip
}: {
    pagination: LendingStrategyPagination;
    principalGroupKey: AllValueType | string | number;
    skip?: boolean;
}): {
    positions: StrategyExpandedInfo[] | undefined;
    totalCount: number | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const principalMint = isAll(principalGroupKey) ? undefined : String(principalGroupKey);

    const { page, pageSize, sortDirection, sortType } = pagination;
    const filter = useMemo(
        (): Partial<LendingStrategyPagination> => ({
            page,
            pageSize,
            sortDirection,
            sortType,
            userAddress: activeWallet,
            principalMints: principalMint ? [principalMint] : [],
            addresses: [],
            showArchived: false
        }),
        [page, pageSize, sortDirection, sortType, activeWallet, principalMint]
    );

    const { data, totalStrategies, isLoading, isFetching } = useStrategies(filter, skip || !activeWallet);

    return { positions: data, totalCount: totalStrategies, isLoading, isFetching };
}

export type UserStrategiesResult = {
    groups: UserStrategyGroup[] | undefined;
    totalGroups: number | undefined;
    aggregate: StrategiesAggregate | undefined;
    snapshotTs: number | undefined;
    isLoading: boolean;
    isFetching: boolean;
};

export function useUserStrategies(filter: Partial<UserStrategiesFilter>, skip?: boolean): UserStrategiesResult {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const rawFilter = useMemo((): UserStrategiesFilter => {
        return {
            userAddress: activeWallet,
            principalMints: filter.principalMints ?? [],
            showArchived: filter.showArchived ?? false,
            sortDirection: filter.sortDirection ?? SortDirection.Desc,
            page: filter.page ?? 0,
            pageSize: filter.pageSize ?? 25
        };
    }, [activeWallet, filter.principalMints, filter.showArchived, filter.sortDirection, filter.page, filter.pageSize]);

    const shouldSkip = !activeWallet || skip;
    const { data, isLoading: queryLoading, isFetching } = useUserStrategiesQuery(rawFilter, { skip: shouldSkip });

    const { tokensLoading, getMetadata } = useAbfTypesToUiConverter();
    const isLoading = queryLoading || tokensLoading;

    const groups = useMemo((): UserStrategyGroup[] | undefined => {
        if (isLoading) return undefined;
        if (shouldSkip || !data) return [];
        return data.groups
            .map((g): NullableRecord<UserStrategyGroup> => ({
                principalMint: g.principalMint,
                principalMetadata: getMetadata(g.principalMint),
                aggregate: g.aggregate,
                principalDeployedNative: Number(g.principalDeployedNative) || 0,
                strategyBalanceNative: Number(g.strategyBalanceNative) || 0,
                totalSupplyNative: Number(g.totalSupplyNative) || 0
            }))
            .filter(filterNullableRecord);
    }, [isLoading, shouldSkip, data, getMetadata]);

    return {
        groups: isLoading ? undefined : groups,
        totalGroups: data?.pageInfo.totalItems,
        aggregate: data?.aggregate,
        snapshotTs: data?.snapshotTs,
        isLoading,
        isFetching
    };
}
